import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { useTonPlans } from '../plans'
import { fetchMachineDetailsFromAPI } from './fetchMachineDetailsFromAPI'
import { fetchMachineDetailsFromCMS } from './fetchMachineDetailsFromCMS'

export type MachineDetailsFromCMS = Awaited<
  ReturnType<typeof fetchMachineDetailsFromCMS>
>[0]

export type MachineDetailsFromAPI = Awaited<
  ReturnType<typeof fetchMachineDetailsFromAPI>
>[0]

export type MachinesByCatalogMap = Map<string, MachineDetailsFromAPI[]>
export type MachinesByNameMap = Map<string, MachineDetailsFromCMS>

interface Context {
  machinesByCatalog: MachinesByCatalogMap
  machinesDetailsByRefID: MachinesByNameMap
  getMachinesSortedByASC(
    machines: MachineDetailsFromAPI[]
  ): MachineDetailsFromAPI[]
  maxAvailableDiscount: number
}

const initialContext = {
  machinesByCatalog: new Map(),
  machinesDetailsByRefID: new Map()
} as Context

const TonMachinesContext = React.createContext(initialContext)

export const useTonMachines = () => {
  return useContext(TonMachinesContext)
}

export interface TonMachinesProviderProps {
  children: React.ReactNode
  machinesDetailsFromAPI: MachineDetailsFromAPI[]
  machinesDetailsFromCMS: MachineDetailsFromCMS[]
}

export const TonMachinesProvider = ({
  children,
  machinesDetailsFromAPI,
  machinesDetailsFromCMS
}: TonMachinesProviderProps) => {
  const { filteredPlansByName } = useTonPlans()

  const [machinesByCatalog, setMachinesByCatalog] = useState(
    initialContext.machinesByCatalog
  )
  const [machinesDetailsByRefID, setmachinesDetailsByRefID] = useState(
    initialContext.machinesDetailsByRefID
  )

  useEffect(() => {
    async function setup() {
      const newMachinesDetailsByRefID = new Map(
        machinesDetailsFromCMS.map((machine) => [machine.refID, machine])
      )

      const newMachinesByCatalog = Map.groupBy(
        machinesDetailsFromAPI,
        (machine) => {
          return machine.catalog
        }
      )

      setMachinesByCatalog(newMachinesByCatalog)
      setmachinesDetailsByRefID(newMachinesDetailsByRefID)
    }

    setup()
  }, [machinesDetailsFromAPI, machinesDetailsFromCMS])

  const getMachinesSortedByASC = useCallback(
    (machines: MachineDetailsFromAPI[]) => {
      const orderToFollow: Record<string, number> = {}

      for (const [refID, { order }] of machinesDetailsByRefID.entries()) {
        orderToFollow[refID] = order
      }

      const _machines = [...machines]

      return _machines.sort((a, b) => {
        const orderA = orderToFollow[a.refID]
        const orderB = orderToFollow[b.refID]

        return orderA - orderB
      })
    },
    [machinesDetailsByRefID]
  )

  const maxAvailableDiscount = useMemo(() => {
    const transactionsPlansByName = [...new Set(filteredPlansByName.keys())]

    return transactionsPlansByName.reduce((acc, planName) => {
      const plan = filteredPlansByName.get(planName)

      if (!plan || plan.type !== 'transactional') return acc

      const machines = machinesByCatalog.get(plan.catalog || '') || []

      const maxDiscountForPlan = Math.max(
        ...machines.map((machine) => Number(machine.discount_percentage) || 0)
      )

      return Math.max(acc, maxDiscountForPlan)
    }, 0)
  }, [filteredPlansByName, machinesByCatalog])

  return (
    <TonMachinesContext.Provider
      value={{
        machinesDetailsByRefID,
        machinesByCatalog,
        getMachinesSortedByASC,
        maxAvailableDiscount
      }}
    >
      {children}
    </TonMachinesContext.Provider>
  )
}
